import { supabase } from '../supabase';

/**
 * Uploads a file to Supabase Storage or a local fallback API.
 * Returns the public URL of the uploaded file.
 */
export const uploadFile = async (file: File | Blob, fileName?: string): Promise<string> => {
  const name = fileName || (file as File).name || `upload_${Date.now()}`;
  
  // Prefer local upload first to avoid Supabase Storage egress.
  // Supabase free tier has only 5 GB egress — file downloads burn through it fast.
  // Local server serves files from disk, using zero Supabase bandwidth.
  // Falls back to Supabase Storage if the local server is unreachable.
  const localUrl = await tryLocalUpload(file, name);
  if (localUrl) return localUrl;

  // Local failed — try Supabase Storage as fallback
  const supabaseUrl = await trySupabaseUpload(file, name);
  if (supabaseUrl) return supabaseUrl;
  
  // Last resort: throw error. Storing Base64 in the database causes massive database egress.
  throw new Error('Upload failed: both local upload and Supabase Storage fallback are unavailable.');
};

async function tryLocalUpload(file: File | Blob, name: string): Promise<string | null> {
  try {
    const reader = new FileReader();
    const base64Data = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Get auth session and attach token to request
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch('/api/upload', {
      method: 'POST',
      headers,
      body: JSON.stringify({ fileName: name, fileData: base64Data }),
    });

    if (!response.ok) {
      console.warn('Local upload failed:', response.status);
      return null;
    }

    const data: { url?: string } = await response.json();
    if (!data.url) {
      console.warn('Local upload response missing url');
      return null;
    }
    return data.url;
  } catch (err) {
    console.warn('Local upload error:', err);
    return null;
  }
}

async function trySupabaseUpload(file: File | Blob, name: string): Promise<string | null> {
  const isSupabaseConfigured =
    import.meta.env.VITE_SUPABASE_URL &&
    !import.meta.env.VITE_SUPABASE_URL.includes('missing-supabase-url');
  if (!isSupabaseConfigured) return null;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const filePath = `${user.id}/${Date.now()}_${name}`;

    // Long-lived immutable cache: file paths embed a Date.now() prefix so
    // each upload is unique — repeat views then hit browser/CDN cache
    // (cached egress) instead of burning uncached egress against the free
    // tier's 5GB cap. Was '3600' (1h) which forced a re-download every hour.
    const { error } = await supabase.storage
      .from('uploads')
      .upload(filePath, file, { cacheControl: '31536000', upsert: false });

    if (error) {
      console.warn('Supabase upload failed:', error.message);
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('uploads')
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (err) {
    console.warn('Supabase upload error:', err);
    return null;
  }
}
